import React, { useState, useEffect } from 'react';
import Table from '../Table/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faUpload, faCheckCircle, faExclamationCircle, faFile, faTimes } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../../pages/keycloak/axiosInstance';

// ─── Toast Notification Component ───────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 40, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 20, scale: 0.95 }}
    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl border backdrop-blur-sm max-w-sm
      ${type === 'success'
        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
        : 'bg-red-50 border-red-200 text-red-800'
      }`}
  >
    <FontAwesomeIcon
      icon={type === 'success' ? faCheckCircle : faExclamationCircle}
      className={`text-lg shrink-0 ${type === 'success' ? 'text-emerald-500' : 'text-red-500'}`}
    />
    <p className="text-sm font-medium leading-snug">{message}</p>
    <button onClick={onClose} className="ml-2 opacity-50 hover:opacity-100 transition-opacity">
      <FontAwesomeIcon icon={faTimes} className="text-xs" />
    </button>
  </motion.div>
);

// ─── Inline Error Banner ─────────────────────────────────────────────────────
const ErrorBanner = ({ message }) => (
  <motion.div
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: 'auto' }}
    exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.25 }}
    className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mt-3"
  >
    <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 mt-0.5 shrink-0" />
    <span className="font-medium">{message}</span>
  </motion.div>
);

// ─── Section Card ────────────────────────────────────────────────────────────
const SectionCard = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 mb-4"
  >
    {children}
  </motion.div>
);

const DesignInput = () => {
  const [modules, setModules] = useState('');
  const [rows, setRows] = useState([
    { fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }
  ]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileTableData, setFileTableData] = useState([]);

  // Toast state
  const [toast, setToast] = useState(null); // { message, type }

  // Error states
  const [submitError, setSubmitError] = useState('');
  const [uploadError, setUploadError] = useState('');

  // Role checks
  const [canEditDesignInput, setCanEditDesignInput] = useState(false);
  const [canDeleteDesignInput, setCanDeleteDesignInput] = useState(false);

  const projectId = localStorage.getItem('selectedProject');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const validateDesignInput = () => {
    if (!modules || modules.trim() === '' || isNaN(modules) || Number(modules) <= 0)
      return 'Number of Modules in a String is required and must be a positive number.';
    if (rows.length === 0) return 'At least one table row is required.';

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.fdnCount?.trim()) return `FDN Count is required for row ${i + 1}.`;
      if (!row.noOfStrings?.trim()) return `No of Strings is required for row ${i + 1}.`;
      if (!row.defineMatrixRows?.trim()) return `Matrix Rows is required for row ${i + 1}.`;
      if (!row.defineMatrixCols?.trim()) return `Matrix Cols is required for row ${i + 1}.`;
      if (!row.defineTolerance?.trim()) return `Tolerance is required for row ${i + 1}.`;
      if (!row.tableType) return `Table Type must be selected for row ${i + 1}.`;
      if (isNaN(row.fdnCount) || Number(row.fdnCount) <= 0) return `FDN Count must be a positive number for row ${i + 1}.`;
      if (isNaN(row.noOfStrings) || Number(row.noOfStrings) <= 0) return `No of Strings must be a positive number for row ${i + 1}.`;
      if (isNaN(row.defineMatrixRows) || Number(row.defineMatrixRows) <= 0) return `Matrix Rows must be a positive number for row ${i + 1}.`;
      if (isNaN(row.defineMatrixCols) || Number(row.defineMatrixCols) <= 0) return `Matrix Cols must be a positive number for row ${i + 1}.`;
      if (isNaN(row.defineTolerance)) return `Tolerance must be a valid number for row ${i + 1}.`;
    }
    return null;
  };

  useEffect(() => {
    const storedRoles = sessionStorage.getItem('userRoles');
    if (storedRoles) {
      let roles = [];
      try {
        const parsed = JSON.parse(storedRoles);
        if (Array.isArray(parsed)) roles = parsed;
      } catch {
        roles = storedRoles.split(',').map(r => r.trim());
      }
      setCanEditDesignInput(roles.includes('Design_Input_Edit'));
      setCanDeleteDesignInput(roles.includes('Design_Input_Delete'));
    }
    fetchUploadedFiles();
    fetchDesignInputData();
  }, []);

  const fetchUploadedFiles = async () => {
    try {
      const response = await apiClient.get(`get_fdn_file`, { params: { projectId } });
      setFileTableData(response.data.files);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
    }
  };

  const fetchDesignInputData = async () => {
    try {
      const response = await apiClient.get(`get_design_input`, { params: { projectId } });
      if (response.data) {
        setModules(response.data.modules || '');
        if (response.data.tableData?.length > 0) {
          setRows(response.data.tableData.map(row => ({
            fdnCount: row.fdnCount || '',
            noOfStrings: row.noOfStrings || '',
            defineMatrixRows: row.defineMatrixRows || row.defineMatrix?.split(' x ')[0] || '',
            defineMatrixCols: row.defineMatrixCols || row.defineMatrix?.split(' x ')[1] || '',
            tableType: row.tableType || '',
            defineTolerance: row.defineTolerance || ''
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching design input data:', error);
    }
  };

  const handleFileUpload = (e) => {
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    setUploadError('');
    const validFiles = Array.from(e.target.files).filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`"${file.name}" is not a valid Excel/CSV file. Please upload .xlsx, .xls, or .csv files only.`);
        return false;
      }
      return true;
    });
    setUploadedFiles(validFiles);
  };

  const removeSelectedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const addRow = () =>
    rows.length < 3 &&
    setRows([...rows, { fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }]);

  const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

  const handleRowChange = (index, field, value) => {
    const newRowData = [...rows];
    newRowData[index][field] = value;
    setRows(newRowData);
  };

  const handleDropdownChange = (index, value) => {
    const newRows = [...rows];
    newRows[index].tableType = value;
    setRows(newRows);
  };

  const getAvailableOptions = (index) => {
    const selectedOptions = rows.map((row, i) => (i !== index ? row.tableType : null)).filter(Boolean);
    return ['Full Table', 'Half Table', 'Quarter Table'].filter(
      option => !selectedOptions.includes(option) || option === rows[index].tableType
    );
  };

  const handleSubmit = async () => {
    const validationError = validateDesignInput();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitError('');
    const currentDate = new Date().toISOString();
    const data = {
      projectId, modules,
      tableData: rows.map(row => ({ ...row, defineMatrix: `${row.defineMatrixRows} x ${row.defineMatrixCols}` })),
      createdBy: sessionStorage.getItem('username'),
      createdDate: currentDate,
      updatedBy: sessionStorage.getItem('username'),
      updatedDate: currentDate
    };
    try {
      await apiClient.post(`create_or_update_design_input`, data);
      showToast('Table data submitted successfully!', 'success');
      fetchDesignInputData();
    } catch (error) {
      const apiError = error?.response?.data?.error || 'Failed to submit data. Please try again.';
      setSubmitError(apiError);
    }
  };

  const handleFileSubmit = async () => {
    if (uploadedFiles.length === 0) {
      setUploadError('Please select at least one file before uploading.');
      return;
    }
    setUploadError('');
    const data = new FormData();
    uploadedFiles.forEach(file => data.append('file', file));
    data.append('projectId', projectId);
    try {
      await apiClient.post(`upload_fdn_csv`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('Files uploaded successfully!', 'success');
      fetchUploadedFiles();
      setUploadedFiles([]);
    } catch (error) {
      const apiError = error?.response?.data?.error || 'Failed to upload files. Please try again.';
      setUploadError(apiError);
    }
  };

  const handleFileDelete = async (fileName) => {
    const extractedFileName = fileName.split('/').pop();

    // Optimistically remove from UI immediately
    setFileTableData(prev => prev.filter(file => file.file_name !== fileName));

    try {
      await apiClient.delete(`delete_fdn_file`, {
        params: { projectId, fileName: extractedFileName }
      });
      showToast('File deleted successfully!', 'success');
      fetchUploadedFiles(); // sync with server to confirm
    } catch (error) {
      // Restore the file in UI if delete failed
      fetchUploadedFiles();
      showToast('Failed to delete file. Please try again.', 'error');
    }
  };

  const tableHeaders2 = [
    { dbParam: 'srNo', displayName: 'Sr. No' },
    { dbParam: 'uploadedFileName', displayName: 'Uploaded File Name' },
    { dbParam: 'uploadedDate', displayName: 'Uploaded Date' },
    { dbParam: 'download', displayName: 'Download' },
    ...(canDeleteDesignInput ? [{ dbParam: 'delete', displayName: 'Delete' }] : [])
  ];

  const tableData2 = fileTableData.map((file, index) => ({
    srNo: index + 1,
    uploadedFileName: file.file_name,
    uploadedDate: file.last_modified,
    download: file.url ? (
      <a href={file.url} className="text-blue-600 hover:underline font-medium text-sm">Download</a>
    ) : '—',
    ...(canDeleteDesignInput ? {
      delete: (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => handleFileDelete(file.file_name)}
          className="text-red-500 hover:text-red-700 transition-colors p-1.5 rounded-lg hover:bg-red-50"
        >
          <FontAwesomeIcon icon={faTrash} />
        </motion.button>
      )
    } : {})
  }));

  const inputClass = "w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 bg-white text-gray-800 text-sm transition-all duration-150 placeholder:text-gray-400";

  return (
    <div className="w-full mx-auto px-4 py-6">
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
      </AnimatePresence>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-6"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight">Design Input</h1>
        <p className="text-gray-500 text-sm mt-1">Configure module parameters and upload foundation data files.</p>
      </motion.div>

      {/* ── Modules Section ── */}
      <SectionCard delay={0}>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Number of Modules in a String
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="number"
          required
          value={modules}
          onChange={(e) => setModules(e.target.value)}
          className="w-full md:w-72 p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all duration-200 bg-white text-gray-800 text-sm placeholder:text-gray-400"
          placeholder="e.g. 24"
          min="1"
        />
      </SectionCard>

      {/* ── Table Definition Section ── */}
      <SectionCard delay={0.1}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Define Table</h2>
            <p className="text-xs text-gray-400 mt-0.5">Up to 3 rows allowed</p>
          </div>
          {rows.length < 3 && (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={addRow}
              className="flex items-center gap-2 bg-[#424752] hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="text-xs" />
              Add Row
            </motion.button>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#424752] text-white text-xs uppercase tracking-wide">
                {['FDN Count', 'No. of Strings', 'Matrix (Rows × Cols)', 'Tolerance (±)', 'Table Type', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left font-semibold first:rounded-tl-xl last:rounded-tr-xl">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              <AnimatePresence>
                {rows.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.25 }}
                    className="bg-white hover:bg-gray-50/70 transition-colors"
                  >
                    <td className="px-3 py-3 w-34">
                      <input type="number" value={row.fdnCount}
                        onChange={(e) => handleRowChange(index, 'fdnCount', e.target.value)}
                        className={inputClass} placeholder="Count" min="1" />
                    </td>
                    <td className="px-3 py-3 w-34">
                      <input type="number" value={row.noOfStrings}
                        onChange={(e) => handleRowChange(index, 'noOfStrings', e.target.value)}
                        className={inputClass} placeholder="Strings" min="1" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <input type="number" value={row.defineMatrixRows}
                          onChange={(e) => handleRowChange(index, 'defineMatrixRows', e.target.value)}
                          className="w-16 p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 bg-white text-center text-sm transition-all"
                          placeholder="R" min="1" />
                        <span className="text-gray-400 font-medium text-sm">×</span>
                        <input type="number" value={row.defineMatrixCols}
                          onChange={(e) => handleRowChange(index, 'defineMatrixCols', e.target.value)}
                          className="w-16 p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 bg-white text-center text-sm transition-all"
                          placeholder="C" min="1" />
                      </div>
                    </td>
                    <td className="px-3 py-3 w-36">
                      <input type="number" value={row.defineTolerance}
                        onChange={(e) => handleRowChange(index, 'defineTolerance', e.target.value)}
                        className={inputClass} placeholder="±" />
                    </td>
                    <td className="px-3 py-3 w-48">
                      <select value={row.tableType}
                        onChange={(e) => handleDropdownChange(index, e.target.value)}
                        className={`${inputClass} cursor-pointer`}>
                        <option value="">Select type</option>
                        {getAvailableOptions(index).map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-center w-14">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => removeRow(index)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-sm" />
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Submit + Errors */}
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {canEditDesignInput ? (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              className="bg-[#424752] hover:bg-gray-600 text-white px-7 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow-sm"
            >
              Save Changes
            </motion.button>
          ) : (
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-lg">
              <FontAwesomeIcon icon={faExclamationCircle} />
              <span>You don't have permission to submit</span>
            </div>
          )}
        </div>

        <AnimatePresence>
          {submitError && <ErrorBanner message={submitError} />}
        </AnimatePresence>
      </SectionCard>

      {/* ── File Upload Section ── */}
      <SectionCard delay={0.2}>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Upload File</h2>

        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <label className="flex-1 w-full cursor-pointer">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
                multiple
              />
              <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl bg-white hover:border-slate-400 transition-all duration-150 cursor-pointer shadow-sm">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <FontAwesomeIcon icon={faFile} className="text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  {uploadedFiles.length > 0 ? (
                    <span className="text-sm text-gray-700 font-medium truncate">
                      {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} selected
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">Choose .xlsx, .xls, or .csv files…</span>
                  )}
                </div>
                <span className="text-xs text-slate-600 font-medium bg-slate-100 px-3 py-1.5 rounded-lg shrink-0">Browse</span>
              </div>
            </label>

            {canEditDesignInput ? (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleFileSubmit}
                className="flex items-center gap-2 bg-[#424752] hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors shadow-sm shrink-0"
              >
                <FontAwesomeIcon icon={faUpload} />
                Upload
              </motion.button>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-lg shrink-0">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>No upload permission</span>
              </div>
            )}
          </div>

          {/* Selected files list */}
          <AnimatePresence>
            {uploadedFiles.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 space-y-2"
              >
                {uploadedFiles.map((file, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center justify-between bg-white border border-gray-100 rounded-lg px-3 py-2 text-sm shadow-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FontAwesomeIcon icon={faFile} className="text-slate-400 shrink-0 text-xs" />
                      <span className="text-gray-700 truncate font-medium">{file.name}</span>
                      <span className="text-gray-400 text-xs shrink-0">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button
                      onClick={() => removeSelectedFile(i)}
                      className="text-gray-300 hover:text-red-500 transition-colors ml-2 shrink-0"
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-xs" />
                    </button>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {uploadError && <ErrorBanner message={uploadError} />}
        </AnimatePresence>
      </SectionCard>

      {/* ── Uploaded Files Table ── */}
      <SectionCard delay={0.3}>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Uploaded Files</h2>
        <Table
          tableHeaders={tableHeaders2}
          tableData={tableData2}
          initialOrder="asc"
          initialOrderBy=""
          handleEditClick={(row) => console.log('Edit row:', row)}
          rowHighlightCondition={() => false}
        />
        {fileTableData.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm">
            <FontAwesomeIcon icon={faFile} className="text-3xl mb-3 block mx-auto opacity-30" />
            No files uploaded yet.
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default DesignInput;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Table from '../Table/Table';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
// import { motion, AnimatePresence } from 'framer-motion';
// import apiClient from '../../pages/keycloak/axiosInstance';

// const DesignInput = () => {
//   const [modules, setModules] = useState('');
//   const [rows, setRows] = useState([
//     { fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }
//   ]);
//   const [uploadedFiles, setUploadedFiles] = useState([]);
//   const [fileTableData, setFileTableData] = useState([]);
//   const [successMessageInput, setSuccessMessageDesignInput] = useState('');
//   const [successMessageUpload, setSuccessMessageUploadFile] = useState('');
//   const [successMessageUploaded, setSuccessMessageUploadedFile] = useState('');

//   // Role checks
//   const [canEditDesignInput, setCanEditDesignInput] = useState(false);
//   const [canDeleteDesignInput, setCanDeleteDesignInput] = useState(false);

//   const baseURL = process.env.REACT_APP_API_BASE_URL;
//   const projectId = localStorage.getItem('selectedProject');
//   const [errorMessage, setErrorMessage] = useState('');

//   const validateDesignInput = () => {
//     // Modules validation
//     if (!modules || modules.trim() === '' || isNaN(modules) || Number(modules) <= 0) {
//       return 'Number of Modules in a String is required and must be a positive number.';
//     }

//     // At least one row required
//     if (rows.length === 0) {
//       return 'At least one table row is required.';
//     }

//     // Validate each row
//     for (let i = 0; i < rows.length; i++) {
//       const row = rows[i];

//       if (!row.fdnCount?.trim()) return `FDN Count is required for row ${i + 1}.`;
//       if (!row.noOfStrings?.trim()) return `No of Strings is required for row ${i + 1}.`;
//       if (!row.defineMatrixRows?.trim()) return `Matrix Rows is required for row ${i + 1}.`;
//       if (!row.defineMatrixCols?.trim()) return `Matrix Cols is required for row ${i + 1}.`;
//       if (!row.defineTolerance?.trim()) return `Tolerance is required for row ${i + 1}.`;
//       if (!row.tableType) return `Table Type must be selected for row ${i + 1}.`;

//       // Optional: stricter numeric validation (recommended)
//       if (isNaN(row.fdnCount) || Number(row.fdnCount) <= 0) {
//         return `FDN Count must be a positive number for row ${i + 1}.`;
//       }
//       if (isNaN(row.noOfStrings) || Number(row.noOfStrings) <= 0) {
//         return `No of Strings must be a positive number for row ${i + 1}.`;
//       }
//       if (isNaN(row.defineMatrixRows) || Number(row.defineMatrixRows) <= 0) {
//         return `Matrix Rows must be a positive number for row ${i + 1}.`;
//       }
//       if (isNaN(row.defineMatrixCols) || Number(row.defineMatrixCols) <= 0) {
//         return `Matrix Cols must be a positive number for row ${i + 1}.`;
//       }
//       // Tolerance could be zero or negative depending on requirements; adjust if needed
//       if (isNaN(row.defineTolerance)) {
//         return `Tolerance must be a valid number for row ${i + 1}.`;
//       }
//     }

//     return null; // All valid
//   };

//   // Check user roles from sessionStorage
//   useEffect(() => {
//     const storedRoles = sessionStorage.getItem("userRoles");
//     if (storedRoles) {
//       let roles = [];
//       try {
//         const parsed = JSON.parse(storedRoles);
//         if (Array.isArray(parsed)) roles = parsed;
//       } catch {
//         roles = storedRoles.split(",").map(r => r.trim());
//       }
//       setCanEditDesignInput(roles.includes("Design_Input_Edit"));
//       setCanDeleteDesignInput(roles.includes("Design_Input_Delete"));
//     }
//   }, []);

//   useEffect(() => {
//     fetchUploadedFiles();
//   }, []);

//   const fetchUploadedFiles = async () => {
//     try {
//       const response = await apiClient.get(`get_fdn_file`, {
//         params: { projectId }
//       });
//       setFileTableData(response.data.files);
//     } catch (error) {
//       console.error('Error fetching uploaded files:', error);
//     }
//   };

//   useEffect(() => {
//     fetchDesignInputData();
//   }, []);

//   const fetchDesignInputData = async () => {
//     try {
//       const response = await apiClient.get(`get_design_input`, {
//         params: { projectId }
//       });

//       if (response.data) {
//         setModules(response.data.modules || '');
//         if (response.data.tableData && response.data.tableData.length > 0) {
//           const formattedRows = response.data.tableData.map(row => ({
//             fdnCount: row.fdnCount || '',
//             noOfStrings: row.noOfStrings || '',
//             defineMatrixRows: row.defineMatrixRows || row.defineMatrix?.split(' x ')[0] || '',
//             defineMatrixCols: row.defineMatrixCols || row.defineMatrix?.split(' x ')[1] || '',
//             tableType: row.tableType || '',
//             defineTolerance: row.defineTolerance || ''
//           }));
//           setRows(formattedRows);
//         }
//       }
//     } catch (error) {
//       console.error('Error fetching design input data:', error);
//     }
//   };

//   const handleFileUpload = (e) => {
//     setUploadedFiles(Array.from(e.target.files))
//       const allowedTypes = [
//         'application/vnd.ms-excel',                                          // .xls
//         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
//         'text/csv',                                                          // .csv
//       ];
//       const files = Array.from(e.target.files).filter(file => {
//         if (!allowedTypes.includes(file.type)) {
//           alert(`"${file.name}" is not a valid Excel file.`);
//           return false;
//         }
//         return true;
//       });
//   };

//   const addRow = () => rows.length < 3 && setRows([...rows, { fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }]);

//   const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

//   const handleRowChange = (index, field, value) => {
//     const newRowData = [...rows];
//     newRowData[index][field] = value;
//     setRows(newRowData);
//   };

//   const handleDropdownChange = (index, value) => {
//     const newRows = [...rows];
//     newRows[index].tableType = value;
//     setRows(newRows);
//   };

//   const getAvailableOptions = (index) => {
//     const selectedOptions = rows
//       .map((row, i) => (i !== index ? row.tableType : null))
//       .filter(Boolean);
//     const allOptions = ['Full Table', 'Half Table', 'Quarter Table'];
//     return allOptions.filter(option =>
//       !selectedOptions.includes(option) || option === rows[index].tableType
//     );
//   };

//   const handleSubmit = async () => {
//     const validationError = validateDesignInput();

//     if (validationError) {
//       setErrorMessage(validationError);
//       setTimeout(() => setErrorMessage(''), 5000);
//       return;
//     }

//     // Clear any previous error
//     setErrorMessage('');

//     const currentDate = new Date().toISOString();

//     const data = {
//       projectId,
//       modules,
//       tableData: rows.map(row => ({
//         ...row,
//         defineMatrix: `${row.defineMatrixRows} x ${row.defineMatrixCols}`
//       })),
//       createdBy: sessionStorage.getItem('username'),
//       createdDate: currentDate,
//       updatedBy: sessionStorage.getItem('username'),
//       updatedDate: currentDate
//     };

//     try {
//       const response = await apiClient.post(`create_or_update_design_input`, data);
//       setSuccessMessageDesignInput('Table data submitted successfully!');
//       fetchDesignInputData();
//       setTimeout(() => setSuccessMessageDesignInput(''), 3000);
//     } catch (error) {
//       console.error('Error submitting data:', error);
//       setErrorMessage('Failed to submit data. Please try again.');
//       setTimeout(() => setErrorMessage(''), 5000);
//     }
//   };

//   const handleFileSubmit = async () => {
//     const data = new FormData();
//     uploadedFiles.forEach(file => data.append('file', file));
//     data.append('projectId', projectId);

//     try {
//       const response = await apiClient.post(`upload_fdn_csv`, data, {
//         headers: { 'Content-Type': 'multipart/form-data' }
//       });
//       setSuccessMessageUploadFile('Files uploaded successfully!');
//       fetchUploadedFiles();
//       setUploadedFiles([]);
//       setTimeout(() => setSuccessMessageUploadFile(''), 3000);
//     } catch (error) {
//       console.error('Error uploading files:', error);
//     }
//   };

//   const handleFileDelete = async (fileName) => {
//     const extractedFileName = fileName.split('/').pop();

//     try {
//       await apiClient.delete(`delete_fdn_file`, {
//         params: { projectId, fileName: extractedFileName }
//       });
//       setSuccessMessageUploadedFile('File deleted successfully!');
//       fetchUploadedFiles();
//       setTimeout(() => setSuccessMessageUploadedFile(''), 3000);
//     } catch (error) {
//       console.error('Error deleting file:', error);
//     }
//   };

//   // Conditional headers: Delete column only if user has permission
//   const tableHeaders2 = [
//     { dbParam: 'srNo', displayName: 'Sr. No' },
//     { dbParam: 'uploadedFileName', displayName: 'Uploaded File Name' },
//     { dbParam: 'uploadedDate', displayName: 'Uploaded Date' },
//     { dbParam: 'download', displayName: 'Download' },
//     ...(canDeleteDesignInput ? [{ dbParam: 'delete', displayName: 'Delete' }] : [])
//   ];

//   // Conditional row data: Delete button only if user has permission
//   const tableData2 = fileTableData.map((file, index) => ({
//     srNo: index + 1,
//     uploadedFileName: file.file_name,
//     uploadedDate: file.last_modified,
//     download: file.url ? (
//       <a href={file.url} className="text-gray-600 hover:underline">Link</a>
//     ) : 'No URL',
//     ...(canDeleteDesignInput ? {
//       delete: (
//         <motion.button
//           whileHover={{ scale: 1.1 }}
//           whileTap={{ scale: 0.9 }}
//           onClick={() => handleFileDelete(file.file_name)}
//           className="text-red-600"
//         >
//           <FontAwesomeIcon icon={faTrash} />
//         </motion.button>
//       )
//     } : {})
//   }));

//   return (
//     <div className="w-full flex items-center justify-center">
//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5 }}
//         className="w-full mx-auto"
//       >
//         <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-4">Design Input</h1>

//         {/* Define Modules Section */}
//         <motion.div
//           initial={{ opacity: 0, y: 10 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.4 }}
//           className="bg-gray-50 rounded-xl p-4 mb-2"
//         >
//           <label className="block text-sm font-bold text-gray-700 mb-2">Number of Modules in a String</label>
//           <input
//             type="number"
//             required
//             value={modules}
//             onChange={(e) => setModules(e.target.value)}
//             className="w-full md:w-1/3 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 bg-white text-gray-800"
//             placeholder="Enter number of modules"
//           />
//         </motion.div>

//         {/* Table Definition Section */}
//         <motion.div
//           initial={{ opacity: 0, y: 10 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.4, delay: 0.1 }}
//           className="bg-gray-50 rounded-xl p-6 mb-2"
//         >
//           <h2 className="text-xl font-semibold text-gray-800 mb-4">Define Table</h2>
//           <div className="overflow-x-auto">
//             <table className="w-full border-collapse">
//               <thead>
//                 <tr className="bg-[#424752] text-white text-sm">
//                   {['FDN (Foundation Count)', 'No of Strings', 'Define Matrix', 'Define Tolerance (+/-)', 'Table Type', 'Actions'].map((header, idx) => (
//                     <th key={idx} className="p-3 text-left">{header}</th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 <AnimatePresence>
//                   {rows.map((row, index) => (
//                     <motion.tr
//                       key={index}
//                       initial={{ opacity: 0, y: 10 }}
//                       animate={{ opacity: 1, y: 0 }}
//                       exit={{ opacity: 0, y: -10 }}
//                       transition={{ duration: 0.3 }}
//                       className="bg-white text-sm hover:bg-gray-100"
//                     >
//                       <td className="p-3">
//                         <input
//                           type="number"
//                           value={row.fdnCount}
//                           onChange={(e) => handleRowChange(index, 'fdnCount', e.target.value)}
//                           className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
//                           placeholder="FDN Count"
//                         />
//                       </td>
//                       <td className="p-3">
//                         <input
//                           type="number"
//                           value={row.noOfStrings}
//                           onChange={(e) => handleRowChange(index, 'noOfStrings', e.target.value)}
//                           className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
//                           placeholder="No of Strings"
//                         />
//                       </td>
//                       <td className="p-3 flex items-center gap-2">
//                         <input
//                           type="number"
//                           value={row.defineMatrixRows}
//                           onChange={(e) => handleRowChange(index, 'defineMatrixRows', e.target.value)}
//                           className="w-16 p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white text-center"
//                           placeholder="Rows"
//                         />
//                         <span className="text-gray-500">x</span>
//                         <input
//                           type="number"
//                           value={row.defineMatrixCols}
//                           onChange={(e) => handleRowChange(index, 'defineMatrixCols', e.target.value)}
//                           className="w-16 p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white text-center"
//                           placeholder="Cols"
//                         />
//                       </td>
//                       <td className="p-3">
//                         <input
//                           type="number"
//                           value={row.defineTolerance}
//                           onChange={(e) => handleRowChange(index, 'defineTolerance', e.target.value)}
//                           className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
//                           placeholder="Tolerance"
//                         />
//                       </td>
//                       <td className="p-3">
//                         <select
//                           value={row.tableType}
//                           onChange={(e) => handleDropdownChange(index, e.target.value)}
//                           className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
//                         >
//                           <option value="">Select Table Type</option>
//                           {getAvailableOptions(index).map(option => (
//                             <option key={option} value={option}>{option}</option>
//                           ))}
//                         </select>
//                       </td>
//                       <td className="p-3 text-center">
//                         <motion.button
//                           whileHover={{ scale: 1.1 }}
//                           whileTap={{ scale: 0.9 }}
//                           onClick={() => removeRow(index)}
//                           className="text-[#424752]"
//                         >
//                           <FontAwesomeIcon icon={faTrash} />
//                         </motion.button>
//                       </td>
//                     </motion.tr>
//                   ))}
//                 </AnimatePresence>
//               </tbody>
//             </table>
//           </div>

//           {rows.length < 3 && (
//             <div className="mt-4 text-right">
//               <motion.button
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//                 onClick={addRow}
//                 className="bg-[#424752] text-white px-4 py-2 rounded-lg"
//               >
//                 <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Row
//               </motion.button>
//             </div>
//           )}

//           <div className="mt-6 text-center">
//             {canEditDesignInput ? (
//               <motion.button
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//                 onClick={handleSubmit}
//                 className="bg-[#424752] hover:bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold"
//               >
//                 Submit
//               </motion.button>
//             ) : (
//               <p className="text-red-600 font-medium">You don't have permission to submit</p>
//             )}
//           </div>
//           <div className="mt-4 text-center">
//             {errorMessage && (
//               <motion.p
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 1 }}
//                 className="text-red-600 font-medium"
//               >
//                 {errorMessage}
//               </motion.p>
//             )}
//             {/* If success message isn't displayed elsewhere, add it here too */}
//             {successMessageInput && (
//               <motion.p
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 1 }}
//                 className="text-green-600 font-medium"
//               >
//                 {successMessageInput}
//               </motion.p>
//             )}
//           </div>
//           <AnimatePresence>
//             {successMessageInput && (
//               <motion.div
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 className="text-center text-green-600 font-semibold mt-4"
//               >
//                 {successMessageInput}
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </motion.div>

//         {/* File Upload Section */}
//         <motion.div
//           initial={{ opacity: 0, y: 10 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.4, delay: 0.2 }}
//           className="bg-gray-50 rounded-xl p-6 mb-8"
//         >
//           <h2 className="text-xl font-semibold text-gray-800 mb-2">Upload File</h2>
//           <div className="flex flex-col sm:flex-row items-center gap-4">
//             <input
//               type="file"
//               accept=".xlsx,.xls,.csv"
//               onChange={handleFileUpload}
//               className="w-full sm:w-auto p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
//               multiple
//             />
//             {canEditDesignInput ? (
//               <motion.button
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//                 onClick={handleFileSubmit}
//                 className="bg-[#424752] hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold"
//               >
//                 Upload
//               </motion.button>
//             ) : (
//               <p className="text-red-600 font-medium">You don't have permission to upload</p>
//             )}
//           </div>

//           <AnimatePresence>
//             {successMessageUpload && (
//               <motion.div
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 className="text-center text-green-600 font-semibold mt-4"
//               >
//                 {successMessageUpload}
//               </motion.div>
//             )}
//           </AnimatePresence>

//           {uploadedFiles.length > 0 && (
//             <motion.div
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               className="mt-4 text-sm text-gray-600"
//             >
//               Selected: {uploadedFiles.map(file => file.name).join(', ')}
//             </motion.div>
//           )}
//         </motion.div>

//         {/* Uploaded Files Section */}
//         <motion.div
//           initial={{ opacity: 0, y: 10 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.4, delay: 0.3 }}
//         >
//           <h2 className="w-full text-xl flex justify-center font-semibold text-gray-800 mb-2">Uploaded Files</h2>
//           <AnimatePresence>
//             {successMessageUploaded && (
//               <motion.div
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 className="text-center text-green-600 font-semibold mb-4"
//               >
//                 {successMessageUploaded}
//               </motion.div>
//             )}
//           </AnimatePresence>
//           <Table
//             tableHeaders={tableHeaders2}
//             tableData={tableData2}
//             initialOrder="asc"
//             initialOrderBy=""
//             handleEditClick={(row) => console.log('Edit row:', row)}
//             rowHighlightCondition={(row) => false}
//           />
//         </motion.div>
//       </motion.div>
//     </div>
//   );
// };

// export default DesignInput;